import { useCreateTicketStore } from '@/components/editor/TicketPlugin/TicketPlugin';
import { TicketsStoreProvider } from '@/components/editor/TicketPlugin/TicketsStoreProvider';
import { extractMarkdownReferences } from '@/components/editor/extractMarkdownReferences';
import { EntityForm } from '@/components/forms/EntityForm';
import { ticketDetailLayout } from '@/features/tickets/ticket.schemas';
import { useCurrentUser, useUserMap } from '@/features/users/users.hooks';
import { useEntityForm } from '@/hooks/useEntityForm';
import { getDirtyValues } from '@/lib/form-utils';
import { markdownReferencesToMeta } from '@/lib/markdownReferencesToMeta';
import { processPlaceholderTickets } from '@/lib/processPlaceholderTickets';
import { createTicketEvent, EventMeta, TicketEvent } from '@certhon/models';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { Timeline } from './components/Timeline';
import {
  useClientTickets,
  usePostTicketEventsMutation,
  useReferencedTickets,
  useTicket,
} from './tickets.hooks';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { Badge } from '@/components/ui/Badge/badge';
import { getTicketActions, adaptActionsForPanel } from './tickets.actions';
import { EntityFormHeaderActions } from '@/components/forms/EntityFormHeaderActions';

export const TicketDetailsPanel = ({
  params,
  path,
  basePath,
  title,
  ...panelProps
}: RoutedPanelProps) => {
  const { ticketId } = params;
  const user = useCurrentUser()!;

  const ticketQuery = useTicket(ticketId, { suspense: false });
  const { data: ticket } = ticketQuery;
  // Subject-derived queries read their inputs from the loaded ticket (CRM
  // chain: client_id is a first-class column) — no subject re-fetch.
  const referencedTicketsQuery = useReferencedTickets(
    ticketId,
    ticket?.ticket_references,
  );
  const clientTickets = useClientTickets(ticket?.client_id);

  const ticketsInScope = useMemo(() => {
    const referencedTickets = referencedTicketsQuery.data || [];
    const clientTicketsData = clientTickets.data || [];
    return [...referencedTickets, ...clientTicketsData];
  }, [referencedTicketsQuery.data, clientTickets.data]);

  const ticketsStore = useCreateTicketStore(
    ticketsInScope,
    referencedTicketsQuery.isLoading || clientTickets.isLoading,
  );

  const userMap = useUserMap();

  const entityForm = useEntityForm({
    layout: ticketDetailLayout,

    ...ticketQuery,
  });
  const updateTicketMutation = usePostTicketEventsMutation();

  const actions = useMemo(() => {
    if (!ticket) return [];
    const rowActions = getTicketActions(user?.recnum ?? null);
    return adaptActionsForPanel(rowActions, ticket);
  }, [ticket, user]);

  const panelTitle = ticket
    ? `#${ticket.number} ${ticket.title}`
    : `Ticket ${ticketId.slice(0, 8)}`;

  const statusBadge = ticket && (
    <Badge
      variant={ticket.status === 'opened' ? 'destructive' : 'positive'}
      className="text-xs"
    >
      {ticket.status === 'opened' ? 'Open' : 'Closed'}
    </Badge>
  );

  const headerActions = (
    <>
      {statusBadge}
      <EntityFormHeaderActions
        entityForm={entityForm}
        formId="ticket-details-form"
      />
    </>
  );

  return (
    <Panel
      {...panelProps}
      title={panelTitle}
      actions={entityForm.formMode === 'view' ? actions : undefined}
      headerActions={headerActions}
    >
      <TicketsStoreProvider store={ticketsStore}>
        <EntityForm
          {...entityForm}
          id="ticket-details-form"
          hideActions={true}
          onSubmit={entityForm.form.handleSubmit(
            async () => {
              try {
                const dirtyValues = getDirtyValues(
                  entityForm.form.formState.dirtyFields,
                  entityForm.form.getValues(),
                );

                // A dirty reference sends the whole chain. werk_id may
                // be null (project-level); the other two may not.
                if (
                  'project_id' in dirtyValues ||
                  'werk_id' in dirtyValues ||
                  'client_id' in dirtyValues
                ) {
                  const allValues = entityForm.form.getValues();
                  if (
                    allValues.project_id == null ||
                    allValues.client_id == null
                  ) {
                    throw new Error(
                      'Could not resolve the project and customer for this ticket',
                    );
                  }
                  dirtyValues.project_id = allValues.project_id;
                  dirtyValues.werk_id = allValues.werk_id ?? null;
                  dirtyValues.client_id = allValues.client_id;
                }

                if (dirtyValues.labels) {
                  const add_labels = dirtyValues.labels.filter(
                    (label: number) =>
                      !ticketQuery.data?.labels.includes(label),
                  );
                  const remove_labels = ticketQuery.data?.labels.filter(
                    (label: number) => !dirtyValues.labels.includes(label),
                  );
                  delete dirtyValues.labels;
                  if (add_labels?.length) {
                    dirtyValues.add_labels = add_labels;
                  }
                  if (remove_labels?.length) {
                    dirtyValues.remove_labels = remove_labels;
                  }
                }
                // Process placeholder tickets in description
                const eventsToPost: TicketEvent[] = [];
                if (dirtyValues.description) {
                  const { markdown, ticketEvents } = processPlaceholderTickets(
                    dirtyValues.description,
                    {
                      userId: user.recnum,
                      projectId: ticket?.project_id,
                      werkId: ticket?.werk_id,
                      clientId: ticket?.client_id,
                      originTicketId: ticketId,
                    },
                  );
                  dirtyValues.description = markdown;
                  eventsToPost.push(...ticketEvents);
                }
                // Extract references and build meta
                let meta: EventMeta | undefined;
                if (dirtyValues.description) {
                  const references = extractMarkdownReferences(
                    dirtyValues.description,
                  );
                  if (references.length) {
                    meta = markdownReferencesToMeta(
                      references,
                      userMap,
                      ticketsStore,
                    );
                  }
                }
                // Post placeholder tickets and update atomically
                eventsToPost.push(
                  createTicketEvent('UPDATE_TICKET', {
                    user_id: user.recnum,
                    ticket_id: ticketId,
                    payload: dirtyValues,
                    meta,
                  }),
                );
                await updateTicketMutation.mutateAsync(eventsToPost);
              } catch (error: unknown) {
                toast(
                  error instanceof Error ? error.message : error?.toString(),
                );
              }
              entityForm.setFormMode('view');
            },
            (errors) => {
              console.log('invalid', { errors });
            },
          )}
        />
        <Timeline
          events={ticketQuery.data?.events || []}
          ticketId={ticketId}
          currentUserId={user.recnum}
          referencedTickets={referencedTicketsQuery.data || []}
        />
      </TicketsStoreProvider>
    </Panel>
  );
};
